import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import {
  useGetFaqCategoriesQuery,
  useDeleteFaqCategoryMutation,
  useCreateFaqCategoryMutation,
  useUpdateFaqCategoryMutation,
  useReloadRagMutation,
} from '../redux/services/faqApi';

const FaqCategories = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryType, setCategoryType] = useState(0);
  const [reloadDialogOpen, setReloadDialogOpen] = useState(false);

  const {
    data: categories,
    isLoading,
    error,
    refetch,
  } = useGetFaqCategoriesQuery();

  const [deleteFaqCategory] = useDeleteFaqCategoryMutation();
  const [createFaqCategory] = useCreateFaqCategoryMutation();
  const [updateFaqCategory] = useUpdateFaqCategoryMutation();
  const [reloadRag, { isLoading: isReloading }] = useReloadRagMutation();

  const categoryTypes = [
    { value: 0, label: 'Default' },
    { value: 1, label: 'Sales' },
    { value: 2, label: 'Marafon 1' },
    { value: 3, label: 'Marafon 2' },
    { value: 4, label: 'Health' },
    { value: 5, label: 'Objection' },
    { value: 6, label: 'Programm' },
    { value: 7, label: 'Meal' },
  ];

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteFaqCategory(selectedCategory.id).unwrap();
      showToast('FAQ category deleted successfully', 'success');
      refetch();
    } catch (err) {
      showToast(
        err.data?.message || 'Failed to delete FAQ category', 
        'error'
      );
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
    }
  };

  const handleCreateCategory = async () => {
    try {
      // Prepare data for API - removed description field
      const categoryData = {
        name: categoryName
      };
      
      // Call the createFaqCategory mutation
      await createFaqCategory(categoryData).unwrap();
      
      // Show success message and reset form
      showToast('Category created successfully', 'success');
      setCreateDialogOpen(false);
      setCategoryName('');
      
      // Refetch to update the list
      refetch();
    } catch (error) {
      console.error('Error creating category:', error);
      showToast(error.data?.message || 'Failed to create category', 'error');
    }
  };

  const handleUpdateCategory = async () => {
    try {
      const categoryData = {
        categoryId: selectedCategory.id,
        name: categoryName,
        cat_type: categoryType
      };
      
      await updateFaqCategory(categoryData).unwrap();
      
      showToast('Category updated successfully', 'success');
      setEditDialogOpen(false);
      setCategoryName('');
      setCategoryType(0);
      
      refetch();
    } catch (error) {
      console.error('Error updating category:', error);
      showToast(error.data?.message || 'Failed to update category', 'error');
    }
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setCategoryName(category.name);
    setCategoryType(category.cat_type || 0);
    setEditDialogOpen(true);
  };

  const handleViewFaqs = (categoryId) => {
    navigate(`/faq/${categoryId}`);
  };

  const handleReloadRag = () => {
    setReloadDialogOpen(true);
  };

  const handleReloadConfirm = async () => {
    try {
      await reloadRag().unwrap();
      showToast('RAG system sent for reprocessing', 'success');
    } catch (error) {
      showToast(error.data?.message || 'Failed to reload RAG system', 'error');
    } finally {
      setReloadDialogOpen(false);
    }
  };

  // Extract categories array from API response with better error handling
  const categoriesArray = useMemo(() => {
    if (!categories) return [];
    
    // Handle different possible data structures
    if (Array.isArray(categories)) {
      return categories;
    } else if (categories.items && Array.isArray(categories.items)) {
      return categories.items;
    } else {
      console.warn('Unexpected categories data format:', categories);
      return [];
    }
  }, [categories]);

  // Now safely filter the array with a null check
  const filteredCategories = Array.isArray(categoriesArray) 
    ? categoriesArray.filter(category => 
        category.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Category Name', width: 400 },
    {
      field: 'cat_type',
      headerName: 'Type',
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row) return 'Unknown';
        const type = categoryTypes.find(t => t.value === (params.row.cat_type ?? 0));
        return type ? type.label : 'Unknown';
      }
    },
    {
      field: 'updated',
      headerName: 'Last Updated',
      width: 180,
      valueGetter: (params) => {
        if (!params) return '';
        return new Date(params).toLocaleString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View FAQs">
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleViewFaqs(params.row.id)}
            >
              View FAQs
            </Button>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEditClick(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isLoading || isReloading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="h4" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Error loading FAQ categories
      </Typography>
    );
  }

  return (
    <Paper elevation={1} sx={{ width: '100%', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">FAQ Categories</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReloadRag}
            disabled={isReloading}
          >
            {isReloading ? 'Reloading...' : 'Reload RAG'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Category
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          sx={{ flex: 1, maxWidth: 500 }}
          variant="outlined"
          size="small"
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DataGrid
        rows={filteredCategories}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        disablePagination={true}
        hideFooterPagination={true}
        hideFooter={true}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the category "{selectedCategory?.name}"? 
            This action cannot be undone and will also delete all FAQs in this category.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>Category Type</InputLabel>
            <Select
              value={categoryType}
              onChange={(e) => setCategoryType(e.target.value)}
              label="Category Type"
            >
              {categoryTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateCategory} 
            color="primary" 
            variant="contained"
            disabled={!categoryName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCategory} 
            color="primary" 
            variant="contained"
            disabled={!categoryName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reload Confirmation Dialog */}
      <Dialog
        open={reloadDialogOpen}
        onClose={() => setReloadDialogOpen(false)}
      >
        <DialogTitle>
          Confirm Reload
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reload the RAG system? This may take a few moments.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReloadDialogOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReloadConfirm} 
            color="primary" 
            variant="contained"
            disabled={isReloading}
          >
            {isReloading ? 'Reloading...' : 'Reload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FaqCategories; 